import { Injectable } from "@angular/core";
import { DocumentRef } from "@core/browser/document-ref";

// Shipped with the app (no Google stylesheet needed); treated as already available for loading.
export const BUNDLED_FONTS = ['Roboto', 'Montserrat'];

const GOOGLE_FONTS_BASE = 'https://fonts.googleapis.com/css2';
const DEFAULT_WEIGHTS = [400, 700];

/**
 * Loads report fonts (bundled or on-demand Google families) and exposes a gate so callers can wait
 * until glyphs are ready before painting a Chart.js canvas or printing — both measure the fallback
 * font silently if the real font has not finished loading.
 */
@Injectable()
export class FontLoaderService {

    readonly bundled = new Set<string>(BUNDLED_FONTS);

    private _weightLoads = new Map<string, Promise<void>>();
    private _googleLinks = new Map<string, Promise<void>>();

    constructor(private _documentRef: DocumentRef) {}

    isLoaded(family: string, weight: number = 400): boolean {
        const fonts = this._fonts();
        return fonts ? fonts.check(`${weight} 1em "${family}"`) : true;
    }

    ensure(family: string, weights: number[] = DEFAULT_WEIGHTS): Promise<void> {
        if (!family) return Promise.resolve();

        const stylesheet = this.bundled.has(family)
            ? Promise.resolve()
            : this._ensureGoogleLink(family, weights);

        return Promise.all(weights.map(weight => this._loadWeight(family, weight, stylesheet)))
            .then(() => undefined);
    }

    // Print gate: resolves once every font requested this session has settled.
    whenReady(): Promise<void> {
        return Promise.all(Array.from(this._weightLoads.values())).then(() => undefined);
    }

    private _loadWeight(family: string, weight: number, stylesheet: Promise<void>): Promise<void> {
        const cacheKey = `${family}|${weight}`;
        if (this._weightLoads.has(cacheKey)) return this._weightLoads.get(cacheKey);

        const load = stylesheet.then(() => {
            const fonts = this._fonts();
            if (!fonts || !fonts.load) return undefined;
            return fonts.load(`${weight} 1em "${family}"`).then(() => undefined);
        }).catch(() => undefined);

        this._weightLoads.set(cacheKey, load);
        return load;
    }

    private _ensureGoogleLink(family: string, weights: number[]): Promise<void> {
        if (this._googleLinks.has(family)) return this._googleLinks.get(family);

        const doc = this._documentRef.getNativeDocument();
        const weightParam = [...weights].sort((a, b) => a - b).join(';');
        const href = `${GOOGLE_FONTS_BASE}?family=${encodeURIComponent(family)}:wght@${weightParam}&display=swap`;

        const ready = new Promise<void>(resolve => {
            const link = doc.createElement('link');
            link.rel = 'stylesheet';
            link.href = href;
            // Resolve on error too, so a font-CDN failure can never hang the print gate.
            link.onload = () => resolve();
            link.onerror = () => resolve();
            doc.head.appendChild(link);
        });

        this._googleLinks.set(family, ready);
        return ready;
    }

    // `any` rather than FontFaceSet — the CSS Font Loading API types aren't in this project's TS lib.
    private _fonts(): any {
        const doc = this._documentRef.getNativeDocument() as any;
        return doc && doc.fonts ? doc.fonts : null;
    }
}
